import client from 'client'
import type {
  ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest,
  ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationResponse,
  ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostPathParams,
  ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostQueryParams,
  ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost422,
} from '../../types/ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const resendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationKey = () =>
  [{ url: '/asset_position/{asset_position_id}/files/resend' }] as const

export type ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationKey = ReturnType<
  typeof resendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationKey
>

/**
 * @summary Resend Integration Files For Period
 * {@link /asset_position/:asset_position_id/files/resend}
 */
export async function resendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost(
  asset_position_id: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostPathParams['asset_position_id'],
  data: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest,
  params?: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostQueryParams,
  config: Partial<RequestConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationResponse,
    ResponseErrorConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost422>,
    ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest
  >({ method: 'POST', url: `/asset_position/${asset_position_id}/files/resend`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Resend Integration Files For Period
 * {@link /asset_position/:asset_position_id/files/resend}
 */
export function useResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationResponse>,
      ResponseErrorConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost422>,
      {
        asset_position_id: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostPathParams['asset_position_id']
        data: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest
        params?: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostQueryParams
      }
    >
    client?: Partial<RequestConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? resendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationKey()

  return useMutation<
    ResponseConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationResponse>,
    ResponseErrorConfig<ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost422>,
    {
      asset_position_id: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostPathParams['asset_position_id']
      data: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostMutationRequest
      params?: ResendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPostQueryParams
    }
  >({
    mutationFn: async ({ asset_position_id, data, params }) => {
      return resendIntegrationFilesForPeriodAssetPositionAssetPositionIdFilesResendPost(asset_position_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}